/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useRef, useState, useEffect, useCallback } from "react"
import { Box, IconButton, CircularProgress, Typography } from "@mui/material"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import { MultilangJob } from "src/services/api/document"
import FileCard from "./FileCard"
import { useLogger } from "src/providers/LoggerProvider"

export interface HorizontalCardCarouselProps {
  fetchItems: (offset: number, limit: number) => Promise<{ results: MultilangJob[]; totalCount: number }> // async fetcher with pagination
  cardWidth?: number
  gap?: number
  showArrows?: boolean
  ariaLabel?: string
  showDownloadButton?: boolean
  showDeadline?: boolean
  onInfoClick: (job: MultilangJob) => void
  pageSize?: number
  browserTitle?: string
  onRefreshReady: (refreshFn: () => Promise<void>) => void // Callback to register refresh function
  onOpenEmailModal: () => void
  updatedJob?: MultilangJob
}

export default function HorizontalCardCarousel({
  fetchItems,
  cardWidth = 280,
  gap = 16,
  showArrows = true,
  ariaLabel = "Horizontal card carousel",
  showDownloadButton = false,
  showDeadline = false,
  onInfoClick,
  pageSize = 6,
  browserTitle,
  onRefreshReady,
  onOpenEmailModal,
  updatedJob
}: HorizontalCardCarouselProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollLeftStart = useRef(0)
  const scrollStep = cardWidth + gap
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [items, setItems] = useState<MultilangJob[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [hasMoreData, setHasMoreData] = useState(true)
  const [isUserInteracting, setIsUserInteracting] = useState(false)
  const lastScrollTime = useRef(0)
  const isMountedRef = useRef(true)
  const { logger } = useLogger()
  // Track component mount state
  useEffect(() => {
    isMountedRef.current = true // Reset to true on mount
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!updatedJob) return
    setItems((prev) =>
      prev.map((job) => (job.id === updatedJob.id ? { ...job, externalProjectId: updatedJob.externalProjectId } : job))
    )
  }, [updatedJob])

  // Function to refresh all data without disrupting user experience
  const refreshAllData = useCallback(async () => {
    if (!isMountedRef.current) return // Don't refresh if unmounted
    if (loadingMore || loading || isUserInteracting) return // Don't refresh if already loading or user is interacting

    try {
      // Store current scroll position
      const currentScrollLeft = containerRef.current?.scrollLeft || 0

      // Fetch all currently loaded pages
      const totalItemsToFetch = Math.max((currentPage + 1) * pageSize, pageSize)
      const res = await fetchItems(0, totalItemsToFetch)

      if (!isMountedRef.current) return // Check again after async operation

      setItems(res.results)
      setTotalCount(res.totalCount)
      setHasMoreData(res.results.length < res.totalCount)

      // Restore scroll position after a small delay to allow for rendering
      setTimeout(() => {
        if (containerRef.current && isMountedRef.current) {
          containerRef.current.scrollLeft = currentScrollLeft
        }
      }, 50)
    } catch (error) {
      logger.error("Error refreshing data:", error)
    }
  }, [loadingMore, loading, isUserInteracting, currentPage, pageSize, fetchItems])

  // Register refresh function with parent component
  useEffect(() => {
    onRefreshReady(refreshAllData)
  }, [onRefreshReady, refreshAllData])

  // Fetch initial items
  useEffect(() => {
    let active = true
    setLoading(true)
    setCurrentPage(0)
    setItems([])
    setHasMoreData(true)

    fetchItems(0, pageSize)
      .then((res) => {
        if (active) {
          setItems(res.results)
          setTotalCount(res.totalCount)
          setHasMoreData(res.results.length === pageSize && res.results.length < res.totalCount)
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  // Function to load more items
  const loadMoreItems = async () => {
    if (loadingMore || !hasMoreData) return

    setLoadingMore(true)
    const nextPage = currentPage + 1

    try {
      const res = await fetchItems(nextPage * pageSize, pageSize)
      const newItems = res.results

      if (newItems.length > 0) {
        setItems((prev) => [...prev, ...newItems])
        setCurrentPage(nextPage)
        setTotalCount(res.totalCount)

        // Check if we have more data
        const totalLoaded = (nextPage + 1) * pageSize
        setHasMoreData(totalLoaded < res.totalCount)
      } else {
        setHasMoreData(false)
      }
    } catch (error) {
      console.error("Error loading more items:", error)
    } finally {
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const update = () => {
      setCanScrollLeft(el.scrollLeft > 8)
      setCanScrollRight(el.scrollLeft + el.clientWidth + 8 < el.scrollWidth)

      // Check if we're near the end for infinite scroll
      const nearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 100 // 100px threshold
      if (nearEnd && hasMoreData && !loadingMore) {
        loadMoreItems()
      }

      // Track scroll activity for interaction detection
      const now = Date.now()
      lastScrollTime.current = now

      // Set user interacting when scrolling
      setIsUserInteracting(true)

      // Clear interaction flag after 3 seconds of no scrolling
      setTimeout(() => {
        if (Date.now() - lastScrollTime.current >= 3000) {
          setIsUserInteracting(false)
        }
      }, 3000)
    }

    update()
    el.addEventListener("scroll", update, { passive: true })
    window.addEventListener("resize", update)
    return () => {
      el.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
    }
  }, [items, hasMoreData, loadingMore])

  function scrollByDirection(direction: "right" | "left") {
    const el = containerRef.current
    if (!el) return
    const left = direction === "right" ? scrollStep : -scrollStep
    el.scrollBy({ left, behavior: "smooth" })

    // Pause auto-refresh when user clicks arrows
    setIsUserInteracting(true)
    setTimeout(() => setIsUserInteracting(false), 2000)
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    const el = containerRef.current
    if (!el) return
    isDragging.current = true
    setIsUserInteracting(true)
    el.setPointerCapture(e.pointerId)
    startX.current = e.clientX
    scrollLeftStart.current = el.scrollLeft
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!isDragging.current) return
    const el = containerRef.current
    if (!el) return
    const dx = e.clientX - startX.current
    el.scrollLeft = scrollLeftStart.current - dx
  }
  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    const el = containerRef.current
    if (!el) return
    isDragging.current = false
    el.releasePointerCapture?.(e.pointerId)
    snapToClosest()

    // Re-enable auto-refresh after a short delay
    setTimeout(() => setIsUserInteracting(false), 2000)
  }

  function onPointerCancel(e: React.PointerEvent<HTMLDivElement>) {
    const el = containerRef.current
    if (!el) return
    isDragging.current = false
    el.releasePointerCapture?.(e.pointerId)

    // Re-enable auto-refresh after a short delay
    setTimeout(() => setIsUserInteracting(false), 2000)
  }

  function onPointerLeave(e: React.PointerEvent<HTMLDivElement>) {
    if (isDragging.current) {
      const el = containerRef.current
      if (!el) return
      isDragging.current = false
      el.releasePointerCapture?.(e.pointerId)

      // Re-enable auto-refresh after a short delay
      setTimeout(() => setIsUserInteracting(false), 2000)
    }
  }

  function snapToClosest() {
    const el = containerRef.current
    if (!el) return
    const scroll = el.scrollLeft
    const index = Math.round(scroll / scrollStep)
    const target = index * scrollStep
    el.scrollTo({ left: target, behavior: "smooth" })
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowRight") {
      e.preventDefault()
      scrollByDirection("right")
    } else if (e.key === "ArrowLeft") {
      e.preventDefault()
      scrollByDirection("left")
    }
  }

  function RenderCard({ job }: { job: MultilangJob }) {
    return (
      <Box
        sx={{
          minWidth: `${cardWidth}px`,
          maxWidth: `${cardWidth}px`,
          scrollSnapAlign: "start",
          flex: "0 0 auto"
        }}
      >
        <FileCard
          key={job.id}
          job={job}
          showDownloadButton={showDownloadButton}
          showDeadline={showDeadline}
          onInfoClick={onInfoClick}
          onModalClose={() => {}}
          onOpenEmailModal={onOpenEmailModal}
        />
      </Box>
    )
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2, flex: 1 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ position: "relative", width: "100%", flex: 1, py: 2.5 }}>
      {showArrows && (
        <IconButton
          aria-label="scroll left"
          onClick={() => scrollByDirection("left")}
          disabled={!canScrollLeft}
          sx={{
            position: "absolute",
            left: -8,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            display: { xs: "none", md: "inline-flex" }
          }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>
      )}

      <Box sx={{ px: 2 }}>
        <Typography variant="h5">{browserTitle}</Typography>
      </Box>
      <Box
        ref={containerRef}
        tabIndex={0}
        role="list"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onPointerLeave={onPointerLeave}
        sx={{
          display: "flex",
          gap: `${gap}px`,
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          px: 1,
          py: 1,
          width: "100%",
          maxWidth: "100%",
          "&:focus": { outline: "none" },
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-thumb": { borderRadius: 2 },
          '& li:last-child': {
            mr: '300px'
          }
        }}
      >
        {items.map((job) => (
          <Box key={job.id} component="li" sx={{ listStyle: "none" }}>
            <RenderCard job={job} />
          </Box>
        ))}
        {loadingMore && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: `${cardWidth}px`,
              height: "200px"
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
      </Box>

      {showArrows && (
        <IconButton
          aria-label="scroll right"
          onClick={() => scrollByDirection("right")}
          disabled={!canScrollRight}
          sx={{
            position: "absolute",
            right: -8,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            display: { xs: "none", md: "inline-flex" }
          }}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      )}
    </Box>
  )
}

/*
Example usage:

const fetchJobs = async (offset: number, limit: number): Promise<{ results: MultilangJob[], totalCount: number }> => {
  const res = await fetch(`/api/jobs?offset=${offset}&limit=${limit}`);
  return res.json();
};

<HorizontalCardCarousel 
  fetchItems={fetchJobs} 
  cardWidth={300} 
  gap={16}
  pageSize={6}
  showDownloadButton={true}
  showDeadline={true}
  onModalClose={() => console.log('Modal closed')}
  onInfoClick={(job) => console.log('Info clicked', job)}
/>
*/
